import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { makeFilter } from "@client.components/TopChartFilters/reducer";
import NavigationTabs from "../NavigationTabs";
import {
  getInsights,
  makeInsights,
  markAsRead,
  setDashboardError,
  getInsightEvents,
  getInsightEventResponses,
  setSelectedInsight,
} from "../reducer";
import "../Dashboard.scss";
import TopChartFilters from "@client.components/TopChartFilters";
import NumberOfInsights from "@client.pages/Dashboard/Insights/NumberOfInsights";
import DataTable from "@client.components/DataTable";
import { getInsightsColumns, getNotificationTypeLabelsMap } from "./consts";
import { Checkbox } from "@client.components/Checkbox";
import { FilterType } from "@client.components/TopChartFilters/types";
import {
  makeLastInsightsUpdateAt,
  makeProfile,
  updateLastInsightsViewed,
} from "@client.pages/Login/reducer";
import { useNavigate } from "react-router-dom";
import useModal from "@client.hooks/useModal";
import InsightsModalContent from "./InsightsModalContent";
import Pages from "@client.enums/pages";
import UserNotificationTypes from "@client.enums/userNotificationTypes";

const DashboardInsights = () => {
  const [setTopBarTitle] = useOutletContext();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showModal } = useModal();
  const filter = useSelector(makeFilter);
  const insights = useSelector(makeInsights);
  const lastInsightsUpdateAt = useSelector(makeLastInsightsUpdateAt);
  const [showRead, setShowRead] = useState(true);
  const setPageError = useCallback(
    (err) => {
      dispatch(setDashboardError(err));
    },
    [dispatch]
  );
  const issuer = useSelector(makeProfile);

  useEffect(() => {
    setTopBarTitle(t("components.NavigationBar.dashboard.insights"));
    return () => {
      setPageError(null);
    };
  }, [t, setTopBarTitle, setPageError]);

  useEffect(() => {
    return () => {
      dispatch(updateLastInsightsViewed());
    };
  }, [dispatch]);

  const dataSource = useMemo(() => {
    const map = getNotificationTypeLabelsMap(t);
    const rows = insights.data.map((n) => {
      return {
        ...n,
        typeDescription: map[n.type].type,
        recommendation: map[n.type].getRecommendation(n.id),
        description: map[n.type].getDescription(n.payload),
      };
    });

    let maxCount = 0;
    const typesMap = rows.reduce((map, row) => {
      if (!map.has(row.type)) {
        map.set(row.type, {
          count: 0,
          typeDescription: row.typeDescription,
          type: row.type,
        });
      }
      map.get(row.type).count += 1;
      if (maxCount < map.get(row.type).count) {
        maxCount = map.get(row.type).count;
      }
      return map;
    }, new Map());

    return {
      data: rows.filter((d) => (showRead ? true : !d.isRead)),
      chartData: {
        data: Array.from(typesMap.values()).sort((a, b) => a.type - b.type),
        maxCount,
      },
    };
  }, [insights, t, showRead]);

  const handleMarkAsRead = useCallback(
    async (id, value) => {
      await dispatch(markAsRead({ id, value }));
      dispatch(getInsights(filter));
    },
    [dispatch, filter]
  );

  const handleLoadInsightEvents = useCallback(
    async (data) => {
      // For LowAttendance insights, load event responses instead of events
      if (data.type === UserNotificationTypes.LowAttendance) {
        await dispatch(getInsightEventResponses(data.id));
      } else {
        // For EmployeeOverspendInternal and EmployeeOverspendExternal, load events
        await dispatch(getInsightEvents(data.id));
      }

      const map = getNotificationTypeLabelsMap(t);
      const insightData = map[data.type];

      showModal({
        title: t("pages.Dashboard.Insights.insightDetails", { type: insightData.type }),
        component: <InsightsModalContent description={insightData.getDescription(data.payload)} data={data} />,
        contentClass: `insight-events-modal-content ${data.type === UserNotificationTypes.LowAttendance ? 'insight-events-modal-content-low-attendance' : ''}`,
      });
    },
    [dispatch, showModal, t]
  );

  const handleNavigateToReports = useCallback(
    (insightData) => {
      if (insightData.type !== UserNotificationTypes.AIPredictions) {
        dispatch(setSelectedInsight(insightData));
      }
      navigate(Pages.ReportsMeetings);
    },
    [dispatch, navigate]
  );

  return (
    <div className="dashboard page">
      <NavigationTabs />
      <div className="body">
        <TopChartFilters type={FilterType.insights} />
        <div className="dashboard-content">
          <div className="insights-grid">
            <div className="grid">
              <NumberOfInsights
                onError={setPageError}
                chartData={dataSource.chartData}
              />
              <div className="insights-table">
                <div className="insights-filter">
                  <span>{t("pages.Dashboard.Insights.actionItems")}</span>
                  <div>
                    <Checkbox
                      value={showRead}
                      onChange={() => setShowRead(!showRead)}
                    />
                    <span onClick={() => setShowRead(!showRead)}>
                      {t("pages.Dashboard.Insights.showCheckedItems")}
                    </span>
                  </div>
                </div>
                <DataTable
                  columns={getInsightsColumns(
                    t,
                    lastInsightsUpdateAt,
                    handleMarkAsRead,
                    handleNavigateToReports,
                    handleLoadInsightEvents,
                    issuer.role
                  )}
                  data={dataSource.data}
                  isLoading={insights.load}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardInsights;
